"use client"

import { useClientTranslation } from "@/modules/App/components/i18n/i18n.client"
import { useMediaQuery } from "@/shared/hooks/use-media-query"
import { getStepNumber, ROUTES, ROUTE_PREFIXES } from "@/shared/routing/routes"
import { newSubscriptionFlowService } from "@/store/machine"
import { useActor } from "@xstate/react"
import { usePathname } from "next/navigation"

import { BreadcrumbItem } from "./breadcrumb-item"
import * as styles from "./breadcrumb.styled"

type BreadcrumbProps = {
  className?: string
}

export const Breadcrumb = ({ className }: BreadcrumbProps) => {
  const { t } = useClientTranslation("global", { keyPrefix: "navbar" })
  const [state] = useActor(newSubscriptionFlowService)
  const hasMultiplePets = state.context.quote?.pets && state.context.quote.pets?.length > 1

  const isViewportBiggerThanMobile = useMediaQuery("(min-width: 1024px)")
  const pathname = usePathname()

  const isStateMatchesTribe = pathname === ROUTES.tribe.path
  const isStateMatchesAttributionQuestion = pathname === ROUTES.attributionQuestion.path
  const isStateMatchesCoverage = pathname === ROUTES.coverage.path
  const isStateMatchesPetFlow = pathname.includes(ROUTE_PREFIXES.pet)
  const isStateMatchesUserFlow = pathname.includes(ROUTE_PREFIXES.user) && ![ROUTES.userInformations.path, ROUTES.userBirthInformations.path].some((route) => route === pathname)
  const isStateMatchesPrepackages = pathname === ROUTES.prepackages.path
  const isStateMatchesTribeQuote = pathname === ROUTES.tribeQuote.path
  const isStateMatchesParameters = pathname === ROUTES.healthCoverageParam.path
  const isStateMatchesPricing = pathname === ROUTES.pricing.path
  const isStateMatchesUserInformationsPart2 = pathname === ROUTES.userInformations.path || pathname === ROUTES.userBirthInformations.path
  const isStateMatchesRecap = pathname === ROUTES.recap.path
  const isStateMatchesQuote = isStateMatchesPrepackages || isStateMatchesTribeQuote || isStateMatchesParameters || isStateMatchesPricing

  const forcedStepNumber = isStateMatchesCoverage ? 14 : isStateMatchesTribeQuote ? 12 : getStepNumber(pathname)
  const completedStepsBarWidth = isStateMatchesCoverage ? 100 : isStateMatchesRecap ? 100 : ((forcedStepNumber - 1) / 14) * 100

  const shouldRenderInitial = isStateMatchesTribe || isStateMatchesCoverage || isStateMatchesAttributionQuestion || isStateMatchesPetFlow || isStateMatchesUserFlow || isStateMatchesPrepackages || isStateMatchesTribeQuote || isStateMatchesParameters || (hasMultiplePets && isStateMatchesQuote)

  const isPetStepActive = isStateMatchesPetFlow || isStateMatchesTribe || isStateMatchesAttributionQuestion
  const isUserStepActive = isStateMatchesUserFlow
  const isCoverageActive = isStateMatchesCoverage || isStateMatchesPrepackages || isStateMatchesTribeQuote || isStateMatchesParameters || (isStateMatchesQuote && hasMultiplePets)

  const renderInitialSteps = () => (
    <>
      {/* STEP 1 — PET INFORMATION */}
      <BreadcrumbItem
        variant={isPetStepActive ? "default" : "disabled"}
        stepNumber={1}
        isActive={isPetStepActive}
      >
        {hasMultiplePets ? t("multi-pets") : t("pet-informations")}
      </BreadcrumbItem>

      {/* STEP 2 — USER INFORMATION */}
      <BreadcrumbItem
        variant={isUserStepActive ? "default" : "disabled"}
        stepNumber={2}
        isActive={isUserStepActive}
      >
        {t("user-informations")}
      </BreadcrumbItem>

      {/* STEP 3 — COVERAGE */}
      <BreadcrumbItem
        variant={isCoverageActive ? "default" : "disabled"}
        stepNumber={4}
        isActive={isCoverageActive}
      >
        {t("coverage")}
      </BreadcrumbItem>
    </>
  )

  const renderQuoteSteps = () => (
    <>
      <BreadcrumbItem
        variant={isStateMatchesQuote ? "default" : "disabled"}
        stepNumber={1}
        isActive={isStateMatchesQuote}
      >
        {t("quote")}
      </BreadcrumbItem>

      <BreadcrumbItem
        variant={isStateMatchesUserInformationsPart2 ? "default" : "disabled"}
        stepNumber={2}
        isActive={isStateMatchesUserInformationsPart2}
      >
        {t("user-informations")}
      </BreadcrumbItem>

      <BreadcrumbItem
        variant={isStateMatchesRecap ? "default" : "disabled"}
        stepNumber={4}
        isActive={isStateMatchesRecap}
      >
        {t("recap")}
      </BreadcrumbItem>
    </>
  )

  return (
    <>
      {isViewportBiggerThanMobile ? (
        <>
          <div className={styles.progressBarContainerTop}>
            <div className={styles.progressBarTop}>
              <div
                className={styles.completedStepsBarTop}
                style={{
                  width: `${completedStepsBarWidth}%`,
                  transition: "all 0.3s ease",
                }}
              />
              <div className={styles.activeStepTop}>
                <div className={styles.activeStepIndicatorTop} />
              </div>
              <div className={styles.uncompletedStepsBarTop} />
            </div>

            <nav className={styles.root(className)}>
              {shouldRenderInitial ? renderInitialSteps() : renderQuoteSteps()}
            </nav>
          </div>
        </>
      ) : null}
    </>
  )
}
